import { trackPromise } from 'react-promise-tracker';
import api from '../../../../utils/API';
import { searchMonitoramentosFiltro } from './services';
import { CidadeFiltro, MonitoramentoFiltroCampo, ProdutoReferencia, RedeFiltro } from './types';

export type FilterOption<TValue = number> = {
  label: string;
  value: TValue;
};

export type StringFilterOption = FilterOption<string>;

export type ProdutoFilterOption = FilterOption & {
  produtoNome: string;
  produtoEan: string;
};

export type RedeFilterOption = FilterOption & {
  redeNome: string;
};

export type CidadeFilterOption = FilterOption & {
  cidadeNome: string;
  uf: string;
};

export type ProdutoCatalogoOption = FilterOption<string> & {
  produtoNome: string;
  produtoEan: string;
};

export type RedeCatalogoOption = FilterOption<string> & {
  redeNome: string;
};

export type CidadeCatalogoOption = FilterOption<string> & {
  cidadeNome: string;
};

// Usado só pelo filtro da listagem (FiltersBox) — escopado a produtos/redes já presentes em algum
// monitoramento cadastrado, porque a listagem filtra por produtoIds/redeIds numéricos.
export const searchProdutosFiltro = async (query: string): Promise<ProdutoFilterOption[]> => {
  const response = await searchMonitoramentosFiltro<ProdutoReferencia>(MonitoramentoFiltroCampo.PRODUTO, query);
  return response.data.content.map(item => ({
    label: item.produtoNome,
    value: item.produtoId,
    produtoNome: item.produtoNome,
    produtoEan: item.produtoEan,
  }));
};

export const searchRedesFiltro = async (query: string): Promise<RedeFilterOption[]> => {
  const response = await searchMonitoramentosFiltro<RedeFiltro>(MonitoramentoFiltroCampo.REDE, query);
  return response.data.content.map(item => ({ label: item.redeNome, value: item.redeId, redeNome: item.redeNome }));
};

type PainelGeralFiltroItem = {
  key: string;
  datapoints: number;
};

type PainelGeralProdutoItem = {
  identificador: string;
  descricao: string;
};

const getPainelGeralLoginContext = () => ({
  login: JSON.parse(localStorage.getItem('usuario') ?? 'null')?.email,
  cliente: JSON.parse(localStorage.getItem('cliente') ?? 'null')?.codigo,
});

const searchPainelGeralFiltro = (field: string, query: string): Promise<PainelGeralFiltroItem[]> =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_CLIENTE_URL}/painel_geral/filtros`,
      method: 'POST',
      data: { query, field, addNotFound: true, ...getPainelGeralLoginContext() },
      expectToken: true,
    }).then(resp => resp.data),
    'central-de-monitoramento',
  );

const searchPainelGeralProduto = (query: string): Promise<PainelGeralProdutoItem[]> =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_CLIENTE_URL}/painel_geral/filtros/produto`,
      method: 'POST',
      params: { size: 50 },
      data: { query, field: 'produto', addNotFound: true, ...getPainelGeralLoginContext() },
      expectToken: true,
    }).then(resp => resp.data),
    'central-de-monitoramento',
  );

// Usado pelo Detalhamento do monitoramento (Criar/Editar) — catálogo completo via painel_geral,
// identificado por gtin/nome (sem id numérico), que é o que o back passou a aceitar em produtos[]/redes[].
export const searchProdutosCatalogo = async (query: string): Promise<ProdutoCatalogoOption[]> => {
  const items = await searchPainelGeralProduto(query);
  return items.map(item => ({
    label: `${item.identificador} - ${item.descricao}`,
    value: item.identificador,
    produtoNome: item.descricao,
    produtoEan: item.identificador,
  }));
};

export const searchRedesCatalogo = async (query: string): Promise<RedeCatalogoOption[]> => {
  const items = await searchPainelGeralFiltro('rede', query);
  return items.map(item => ({ label: item.key, value: item.key, redeNome: item.key }));
};

const formatUfLabel = (uf: string): string => uf.replace(/_/g, ' ');

const formatCidadeLabel = (cidadeNome: string, uf: string | null | undefined): string => (uf ? `${cidadeNome} - ${formatUfLabel(uf)}` : cidadeNome);

export const searchCidadesCatalogo = async (query: string): Promise<CidadeCatalogoOption[]> => {
  const items = await searchPainelGeralFiltro('cidade', query);
  return items.map(item => ({ label: formatCidadeLabel(item.key, null), value: item.key, cidadeNome: item.key }));
};

export const searchUfsCatalogo = async (query: string): Promise<StringFilterOption[]> => {
  const items = await searchPainelGeralFiltro('uf', query);
  return items.map(item => ({ label: formatUfLabel(item.key), value: item.key }));
};

export const searchCategoriasCatalogo = async (query: string): Promise<StringFilterOption[]> => {
  const items = await searchPainelGeralFiltro('categoria', query);
  return items.map(item => ({ label: item.key, value: item.key }));
};

export const searchSecoesCatalogo = async (query: string): Promise<StringFilterOption[]> => {
  const items = await searchPainelGeralFiltro('secao', query);
  return items.map(item => ({ label: item.key, value: item.key }));
};

export const searchCidadesFiltro = async (query: string): Promise<CidadeFilterOption[]> => {
  const response = await searchMonitoramentosFiltro<CidadeFiltro>(MonitoramentoFiltroCampo.CIDADE, query);
  return response.data.content.map(item => ({
    label: formatCidadeLabel(item.cidadeNome, item.uf),
    value: item.cidadeId,
    cidadeNome: item.cidadeNome,
    uf: item.uf,
  }));
};

export const searchUfsFiltro = async (query: string): Promise<StringFilterOption[]> => {
  const response = await searchMonitoramentosFiltro<string>(MonitoramentoFiltroCampo.UF, query);
  return response.data.content.map(uf => ({ label: formatUfLabel(uf), value: uf }));
};
